import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Navigate,
  Link,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import {
  useStytchB2BClient,
  useStytchOrganization,
} from '@stytch/react/b2b';
import { purgeAllBrowserTrackerData } from '@nimbalyst/collab-bundle/trackers-ui';
import { ApiError, listTeams, type TeamSummary } from '../api';
import {
  getFreshBrowserSessionJwt,
  getTeamSessionForOrg,
  TeamSessionError,
  type TeamJwt,
  type TeamSession,
} from '../auth/teamJwt';
import { AppShell } from '../components/AppShell';
import { NEW_TEAM_PATH } from '../organization/CreateTeamPage';
import { PanelState, Wordmark } from '../components/Chrome';
import {
  BUTTON_PRIMARY_LG,
  BUTTON_TEXT,
  CALLBACK_CARD,
  CALLBACK_SCREEN,
} from '../controlStyles';
import { reportFirstOpen } from '../analytics/firstOpenSignal';
import { InvitationWelcome } from '../components/InvitationWelcome';
import { NothingSharedYet } from '../components/NothingSharedYet';
import { consoleConfig } from '../config';
import {
  getProjectPersistenceId,
  getProjectRouteId,
  readLastUsedProjectId,
  rememberLastUsedProjectId,
  resolveProjectSelection,
} from '../projectSelection';
import {
  consoleModeForRoute,
  findTeamByRouteKey,
  orgAdminPath,
  orgDocsPath,
  orgRootPath,
  orgRouteSegments,
  resolveConsoleRoute,
  resolveLegacyRedirect,
} from '../routing';
import { AdminRoute } from './AdminRoutes';
import {
  DocsFolderRoute,
  DocsHomeRoute,
  DocsSurfaceError,
  DocsWorkspaceRoute,
  DocumentRoute,
  LegacyWikiRedirect,
  type DocsSurfaceErrorKind,
} from './DocsRoutes';
import { FeedbackRequestRoute } from './FeedbackRoutes';
import { RequestActivityRoute, RequestsIndexRoute, RequestsWorkspaceRoute } from './RequestsRoutes';
import { TrackerSetupRoute } from './TrackerSetupRoutes';
import { WebMarksListRoute } from '../pages/WebMarksListRoute';
import { WebPagesSearchRoute } from '../pages/WebPagesSearchRoute';
import { WebPagesTypesRoute } from '../pages/WebPagesTypesRoute';
import { WebTypedPageRoute } from '../pages/WebTypedPageRoute';
import { WebTypePageRoute } from '../pages/WebTypePageRoute';
import { TrackersViewRoute, TrackersWorkspaceRoute } from './TrackersRoutes';
import { ProjectTrackerSessionProvider, useProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import { RequestsInboxProvider } from '../requests/RequestsInboxContext';
import { QuickOpenProvider } from '../quickOpen/QuickOpenProvider';
import { webCollabScopeKey } from '../collab/WebCollabHost';
import { isTerminalMembershipFailure, pruneTeamDocsScopes } from '../collab/docsScopeTeardown';
import { orgDocumentPath } from '../routing';
import type { OrganizationRouteContext } from './workspaceContext';
import { isEmbedded } from '../embedded/embedMarker';

const LOGOUT_TRACKER_PURGE_TIMEOUT_MS = 2_000;

/** Docs home needs the shared room too, before any document reader mounts. */
function ProjectTrackerConsumer({ children }: { children: ReactNode }) {
  useProjectTrackerSession();
  return children;
}

async function purgeTrackerDataBeforeLogout(): Promise<void> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      purgeAllBrowserTrackerData(),
      new Promise<void>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error('Timed out purging browser tracker data')),
          LOGOUT_TRACKER_PURGE_TIMEOUT_MS,
        );
      }),
    ]);
  } catch (error) {
    console.error('[WorkspaceRoutes] Unable to purge tracker data before logout', error);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

function preferredProject(team: TeamSummary, urlProjectId?: string | null) {
  return resolveProjectSelection(
    team.projects,
    urlProjectId,
    readLastUsedProjectId(team.orgId),
  );
}

function preferredDocsPath(team: TeamSummary): string {
  const project = preferredProject(team);
  return project ? orgDocsPath(team, getProjectRouteId(project)) : orgRootPath(team);
}

function SessionFailure({
  kind,
  detail,
  onSignIn,
}: {
  kind: DocsSurfaceErrorKind;
  detail?: string;
  onSignIn: () => Promise<void>;
}) {
  return (
    <div className="docs-session-failure mt-7 grid justify-items-center gap-4">
      <DocsSurfaceError kind={kind} detail={detail} />
      {kind === 'session-expired' && <button className={BUTTON_PRIMARY_LG} type="button" onClick={() => void onSignIn()}>Sign in again</button>}
    </div>
  );
}

function OrganizationRoute({
  teams,
  reloadTeams,
}: {
  teams: TeamSummary[];
  reloadTeams: () => Promise<void>;
}) {
  const stytch = useStytchB2BClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug = '' } = useParams<{ orgSlug: string }>();
  const team = useMemo(() => findTeamByRouteKey(teams, orgSlug), [orgSlug, teams]);
  const route = useMemo(() => resolveConsoleRoute(location.pathname), [location.pathname]);
  const requestedProjectId = route.kind === 'docs-home' || route.kind === 'docs-folder'
    || route.kind === 'document' || route.kind === 'trackers-home'
    || route.kind === 'page-item' || route.kind === 'page-type' || route.kind === 'wiki' || route.kind === 'wiki-types' || route.kind === 'marks'
    || route.kind === 'trackers-type' || route.kind === 'tracker-item'
    ? route.projectId
    : null;
  const project = useMemo(
    () => team ? preferredProject(team, requestedProjectId) : null,
    [requestedProjectId, team],
  );
  // Reaching a document, tracker item, or request is the activation signal for
  // the whole web-first landing: it is the difference between the invitee
  // arriving and the invitee using the product. Self-limiting to once per
  // session, so this runs on every route change without guarding here.
  useEffect(() => { reportFirstOpen(route); }, [route]);

  const [teamSession, setTeamSession] = useState<TeamSession | null>(null);
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const [trackerShutdown] = useState(() => new AbortController());

  const getScopedTeamSession = useCallback((options?: { forceRefresh?: boolean }): Promise<TeamSession> => {
    if (!team) return Promise.reject(new TeamSessionError('wrong-org', 'This organization is not available.'));
    return getTeamSessionForOrg(
      stytch.session,
      team.orgId,
      consoleConfig.sessionDurationMinutes,
      team.teamMemberId,
      options,
    );
  }, [stytch, team]);

  const getTeamJwt = useCallback(async (options?: { forceRefresh?: boolean }): Promise<TeamJwt> => (
    await getScopedTeamSession(options)
  ).jwt, [getScopedTeamSession]);

  useEffect(() => {
    if (!team) return;
    let cancelled = false;
    setTeamSession(null);
    setSessionError(null);
    getScopedTeamSession().then(nextSession => {
      if (!cancelled) setTeamSession(nextSession);
    }).catch(cause => {
      if (!cancelled) setSessionError(cause instanceof Error ? cause : new Error('Unable to open this organization session.'));
    });
    return () => {
      cancelled = true;
    };
  }, [getScopedTeamSession, team]);

  const removedFromTeam = isTerminalMembershipFailure(sessionError);

  useEffect(() => {
    // Shared Docs titles survive their session on purpose; a removal is where
    // that retention stops being defensible. See `docsScopeTeardown`.
    if (team && removedFromTeam) pruneTeamDocsScopes(team);
  }, [removedFromTeam, team]);

  useEffect(() => {
    if (team && project) {
      rememberLastUsedProjectId(team.orgId, getProjectPersistenceId(project));
    }
  }, [project, team]);

  const logout = useCallback(async () => {
    // Abort listeners dispose synchronously, before any purge can yield to a
    // room broadcast. The aborted signal also prevents late session creation.
    trackerShutdown.abort();
    await purgeTrackerDataBeforeLogout();
    await stytch.session.revoke({ forceClear: true });
    window.location.replace('/');
  }, [stytch, trackerShutdown]);

  const reauthenticate = useCallback(async () => {
    // In the phone app a reload asks the app for a session again; revoking
    // would only add a round trip to a login page that cannot be used there.
    if (!isEmbedded()) await stytch.session.revoke({ forceClear: true });
    window.location.replace(window.location.href);
  }, [stytch]);

  if (!team) {
    const fallback = teams[0];
    return (
      <main className={CALLBACK_SCREEN}>
        <section className={`${CALLBACK_CARD} access-denied-card grid justify-items-center gap-5`}>
          <Wordmark href={fallback ? preferredDocsPath(fallback) : '/'} />
          <DocsSurfaceError kind="no-access" />
          {fallback && <button className={BUTTON_PRIMARY_LG} type="button" onClick={() => navigate(preferredDocsPath(fallback), { replace: true })}>Open an available organization</button>}
          {!isEmbedded() && <button className={BUTTON_TEXT} type="button" onClick={() => void logout()}>Sign out</button>}
        </section>
      </main>
    );
  }

  const mode = consoleModeForRoute(route);
  // The phone app shows Wiki and Trackers only; anything else lands on the Wiki.
  if (isEmbedded() && mode !== 'docs' && mode !== 'trackers') {
    return <Navigate to={preferredDocsPath(team)} replace />;
  }
  const invitationAccepted = new URLSearchParams(location.search).get('invitation') === 'accepted';
  // The inbox wraps the shell rather than the Requests route: the gutter badge
  // has to be right from every mode, and the presence heartbeat has to run
  // wherever the reader is, or a web-only reviewer sitting on a request reads
  // as offline to their desktop teammates. It is inside `frame` so that every
  // path returns the same tree shape — hoisting it to one branch tears the
  // shell down and rebuilds it the moment the team session resolves.
  // ⌘K sits at the same level and for the same reason: the palette reaches
  // documents *and* tracker items, which are mounted by sibling routes, so it
  // cannot live inside either one. It is handed the team session rather than
  // hoisted onto one branch, because it may not be reachable at all without one
  // -- the failure screen below is exactly where a removed member would
  // otherwise press ⌘K and read back cached titles.
  const projectRouteId = project ? getProjectRouteId(project) : null;
  const frame = (content: ReactNode, authorizedSession: TeamSession | null = teamSession) => (
    <RequestsInboxProvider
      orgId={team.orgId}
      orgName={team.name}
      teamMemberId={teamSession?.memberId ?? null}
      getTeamJwt={getTeamJwt}
    >
      <QuickOpenProvider
        teamSession={authorizedSession}
        docsScopeKey={projectRouteId ? webCollabScopeKey(team.orgId, projectRouteId) : null}
        documentPath={documentId => (projectRouteId
          ? orgDocumentPath(team, projectRouteId, documentId)
          : orgRootPath(team))}
        navigate={path => navigate(path)}
      >
        <AppShell
          mode={mode}
          team={team}
          teams={teams}
          docsProjectId={projectRouteId}
          onSelectTeam={nextTeam => navigate(preferredDocsPath(nextTeam))}
          onLogout={logout}
          notice={invitationAccepted
            ? (
              <InvitationWelcome
                orgName={team.name}
                projectName={project?.name ?? null}
                sharedFolderCount={null}
                orgId={team.orgId}
              />
            )
            : undefined}
        >
          {content}
        </AppShell>
      </QuickOpenProvider>
    </RequestsInboxProvider>
  );

  if (sessionError) {
    let kind: DocsSurfaceErrorKind = 'service-unavailable';
    if (sessionError instanceof TeamSessionError) {
      if (sessionError.kind === 'session-expired') kind = 'session-expired';
      else if (sessionError.kind === 'member-mismatch' || sessionError.kind === 'organization-unavailable') kind = 'removed-member';
      else if (sessionError.kind !== 'service-unavailable') kind = 'no-access';
    }
    // Passed explicitly rather than relying on `teamSession` happening to be
    // null here. This branch is the one a removed member sits on, and what
    // makes ⌘K unreachable from it should not be an invariant two effects away.
    return frame(<SessionFailure kind={kind} detail={sessionError.message} onSignIn={reauthenticate} />, null);
  }
  if (!teamSession) return frame(<PanelState>Opening the organization session…</PanelState>);

  const context: OrganizationRouteContext = {
    team,
    teams,
    project,
    teamSession,
    getTeamJwt,
    reloadTeams,
    handleDeleted: logout,
  };
  return frame(
    <ProjectTrackerSessionProvider context={context} shutdownSignal={trackerShutdown.signal}>
      {mode === 'docs' || mode === 'trackers'
        ? <ProjectTrackerConsumer><Outlet context={context} /></ProjectTrackerConsumer>
        : <Outlet context={context} />}
    </ProjectTrackerSessionProvider>,
  );
}

function LegacyRootRedirect({ teams }: { teams: TeamSummary[] }) {
  const { organization } = useStytchOrganization();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const preferenceTeam = (query.get('orgId') && teams.find(team => team.orgId === query.get('orgId')))
    || (organization?.organization_id && teams.find(team => team.orgId === organization.organization_id))
    || teams[0];
  const redirect = resolveLegacyRedirect({
    pathname: location.pathname,
    search: location.search,
    teams,
    activeOrgId: organization?.organization_id,
    selectedProjectId: preferenceTeam ? readLastUsedProjectId(preferenceTeam.orgId) : null,
  });
  return redirect ? <Navigate to={redirect} replace /> : <PanelState tone="empty">Choose an organization.</PanelState>;
}

function OrganizationIndexRedirect() {
  const { team, project, getTeamJwt } = useOutletContext<OrganizationRouteContext>();
  // With no project there is nothing to route to, and this is where an
  // invitee whose inviter shared nothing comes to rest. It used to say "No
  // projects available", which reads as a fault in the product rather than
  // the ordinary state of a new membership.
  return project
    ? <Navigate to={orgDocsPath(team, getProjectRouteId(project))} replace />
    : <NothingSharedYet orgName={team.name} orgId={team.orgId} getTeamJwt={getTeamJwt} />;
}

function AdminFallback() {
  const { team } = useOutletContext<OrganizationRouteContext>();
  return <Navigate to={orgAdminPath(team)} replace />;
}

function OrganizationUnknownRoute({ teams }: { teams: TeamSummary[] }) {
  const location = useLocation();
  const { team } = useOutletContext<OrganizationRouteContext>();
  const redirect = resolveLegacyRedirect({
    pathname: location.pathname,
    search: location.search,
    teams,
    selectedProjectId: readLastUsedProjectId(team.orgId),
  });
  if (redirect) return <Navigate to={redirect} replace />;
  return <DocsSurfaceError kind="unsupported-document-type" detail="This organization URL does not identify a supported console artifact." />;
}

export function WorkspaceRoutes() {
  const stytch = useStytchB2BClient();
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reloadTeams = useCallback(async (signal?: AbortSignal) => {
    const jwt = await getFreshBrowserSessionJwt(stytch.session, consoleConfig.sessionDurationMinutes);
    const nextTeams = await listTeams(jwt, signal);
    setTeams(nextTeams);
  }, [stytch]);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    reloadTeams(controller.signal).catch(cause => {
      if (!controller.signal.aborted) setError(cause instanceof Error ? cause : new Error('Unable to load organizations.'));
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [reloadTeams]);

  if (loading) return <main className={CALLBACK_SCREEN}>Loading organization access…</main>;
  if (error) {
    const expired = (error instanceof TeamSessionError && (
      error.kind === 'session-expired' || error.kind === 'malformed-token'
    )) || (error instanceof ApiError && error.status === 401);
    return (
      <main className={CALLBACK_SCREEN}>
        <section className={CALLBACK_CARD}>
          <DocsSurfaceError kind={expired ? 'session-expired' : 'service-unavailable'} detail={error.message} />
          {expired && <button className={BUTTON_PRIMARY_LG} type="button" onClick={() => void (isEmbedded() ? Promise.resolve() : stytch.session.revoke({ forceClear: true })).then(() => window.location.reload())}>Sign in again</button>}
        </section>
      </main>
    );
  }
  if (teams.length === 0) {
    return (
      <main className={CALLBACK_SCREEN}>
        <PanelState tone="empty"><span className="empty-mark mx-auto mb-4 grid size-12 place-items-center [&>img]:size-full"><img src="/nimbalyst-icon.svg" alt="" width={48} height={48} /></span><h3 className="mb-[0.4rem] text-[1.05rem] font-semibold">No team organizations yet</h3><p className="mx-auto max-w-[30rem] leading-relaxed text-nim-muted">Create a team to start one, or ask a team admin to invite this address.</p><Link className={`${BUTTON_PRIMARY_LG} mt-5`} to={NEW_TEAM_PATH}>Create a team</Link></PanelState>
      </main>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LegacyRootRedirect teams={teams} />} />
      <Route path="/org/:orgSlug" element={<OrganizationRoute teams={teams} reloadTeams={() => reloadTeams()} />}>
        <Route index element={<OrganizationIndexRedirect />} />
        <Route element={<DocsWorkspaceRoute />}>
          <Route path={orgRouteSegments.docsHome} element={<DocsHomeRoute />} />
          <Route path={orgRouteSegments.docsFolder} element={<DocsFolderRoute />} />
          <Route path={orgRouteSegments.document} element={<DocumentRoute />} />
          <Route path={orgRouteSegments.pageItem} element={<WebTypedPageRoute />} />
          <Route path={orgRouteSegments.pageType} element={<WebTypePageRoute />} />
          <Route path={orgRouteSegments.wiki} element={<WebPagesSearchRoute />} />
          <Route path={orgRouteSegments.wikiTypes} element={<WebPagesTypesRoute />} />
          <Route path={orgRouteSegments.marks} element={<WebMarksListRoute />} />
        </Route>
        {/* Shared links from before the Wiki had its own addresses: Pages, and the retired v1 wiki. */}
        {(['legacyPages', 'legacyPagesTypes', 'retiredWikiPage', 'retiredWikiChanges', 'retiredWikiChangeset', 'retiredWikiType', 'retiredDocsWikiHome', 'retiredDocsWikiPage'] as const).map((segment) => (
          <Route key={segment} path={orgRouteSegments[segment]} element={<LegacyWikiRedirect />} />
        ))}
        {/* One tracker room, three entries: the project's trackers, one type
            inside it, and one item. A saved view rides as `?view=` on the
            first, because a view carries its own type selection.

            All three render the same view surface. An item is a pane beside the
            rows rather than a page replacing them, so `TrackersWorkspaceRoute`
            reads `:itemId` and hands the detail to the shell as a slot; the
            element here stays identical across the three so navigating into an
            item reconciles the list instead of remounting it. */}
        <Route element={<TrackersWorkspaceRoute />}>
          <Route path={orgRouteSegments.trackersHome} element={<TrackersViewRoute />} />
          <Route path={orgRouteSegments.trackersType} element={<TrackersViewRoute />} />
          <Route path={orgRouteSegments.trackerItem} element={<TrackersViewRoute />} />
        </Route>
        {/* Tracker setup has its own provider and no trackers frame. */}
        <Route path={orgRouteSegments.trackerSetup} element={<TrackerSetupRoute />} />
        <Route path={orgRouteSegments.trackerSetupTab} element={<TrackerSetupRoute />} />
        {/* One surface, two entries: the list, and a pasted deep link that
            selects a row inside it. */}
        <Route element={<RequestsWorkspaceRoute />}>
          <Route path={orgRouteSegments.requests} element={<RequestsIndexRoute />} />
          <Route path={orgRouteSegments.requestActivity} element={<RequestActivityRoute />} />
          <Route path={orgRouteSegments.feedbackRequest} element={<FeedbackRequestRoute />} />
        </Route>
        <Route path={orgRouteSegments.adminRoot}>
          <Route index element={<AdminRoute section="overview" />} />
          <Route path={orgRouteSegments.adminBilling} element={<AdminRoute section="billing" />} />
          <Route path={orgRouteSegments.adminMembers} element={<AdminRoute section="members" />} />
          <Route path={orgRouteSegments.adminSecurity} element={<AdminRoute section="security" />} />
          <Route path={orgRouteSegments.adminDanger} element={<AdminRoute section="danger" />} />
          <Route path="*" element={<AdminFallback />} />
        </Route>
        <Route path="*" element={<OrganizationUnknownRoute teams={teams} />} />
      </Route>
      <Route path="*" element={<main className={CALLBACK_SCREEN}><PanelState tone="error"><strong>Page not found</strong><span>This URL does not identify a console route.</span></PanelState></main>} />
    </Routes>
  );
}
